import { useEffect, useMemo } from 'react';

import { useGetDisbursementPayoutReports } from 'api';
import type { PayoutReports } from 'api';

import { useOrganisationId, useVirtualAccounts } from 'hooks/context';
import { useGlobalFilters } from 'hooks/useGlobalFilters';
import { useVirtualList } from 'hooks/useVirtualList';

import { DataTable } from 'components/ui/organisms/DataTable';

import { apiParams } from './apiAdapter';
import { getColumns } from './columns';
import type { LocalState } from './localState';

import { EXPORTED_PAYOUTS_TABLE_TEST_ID } from '../constants';

type Props = {
  localState: LocalState;
  updateCursorAfter: (value: string | null) => void;
  downloadReport: (uuid: string) => void;
};

export const ExportedPayoutsTable = ({
  localState,
  updateCursorAfter,
  downloadReport
}: Props) => {
  const organisationId = useOrganisationId<number>();

  const { filters: globalFilters } = useGlobalFilters();

  const { findAccountByAccountNumber } = useVirtualAccounts();

  const { data: exportedPayouts, isFetching } = useGetDisbursementPayoutReports(
    organisationId,
    apiParams({ ...localState, ...globalFilters }),
    { query: { refetchOnMount: true } }
  );

  useEffect(() => {
    updateCursorAfter(null);
  }, [globalFilters, localState.filters, updateCursorAfter]);

  const exportedPayoutsColumns = useMemo(
    () => getColumns(findAccountByAccountNumber, downloadReport),
    [findAccountByAccountNumber, downloadReport]
  );

  const { onScroll, data } = useVirtualList<PayoutReports>({
    data: exportedPayouts?.data,
    uniqueKey: 'uuid',
    isFetching,
    initialRefValue: String(exportedPayouts?.meta?.cursor_next),
    updateCursorCallback: updateCursorAfter,
    resetConditions: [globalFilters, localState.filters, updateCursorAfter],
    cursorNext: exportedPayouts?.meta?.has_next_page
      ? String(exportedPayouts?.meta?.cursor_next)
      : undefined
  });

  return (
    <DataTable
      total={exportedPayouts?.meta?.total_count}
      dataSource={data}
      columns={exportedPayoutsColumns}
      onScroll={onScroll}
      isLoading={isFetching}
      rowKey="uuid"
      tableDataTestId={EXPORTED_PAYOUTS_TABLE_TEST_ID}
    />
  );
};
